import { useCallback, useState } from 'react';
import useToast from './useToast';

interface Options {
  successMessage?: string;
  errorMessage?: string;
}

interface Result<Args extends unknown[], R> {
  run: (...args: Args) => Promise<R>;
  loading: boolean;
  fieldErrors: Record<string, string>;
  resetFieldErrors: () => void;
}

function extractFieldErrors(data: unknown): Record<string, string> {
  if (!data || typeof data !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(data as Record<string, unknown>)) {
    if (key === 'detail') continue;
    if (Array.isArray(val)) out[key] = String(val[0] ?? '');
    else if (typeof val === 'string') out[key] = val;
  }
  return out;
}

function extractErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (!data) return err?.message ?? fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  const fields = extractFieldErrors(data);
  const first = Object.values(fields)[0];
  return first ?? fallback;
}

export default function useResourceMutation<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  options: Options = {}
): Result<Args, R> {
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  const run = useCallback(
    async (...args: Args) => {
      setLoading(true);
      setFieldErrors({});
      try {
        const result = await fn(...args);
        if (options.successMessage) toast.success(options.successMessage);
        return result;
      } catch (err: any) {
        setFieldErrors(extractFieldErrors(err?.response?.data));
        toast.error(extractErrorMessage(err, options.errorMessage ?? 'Request failed'));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fn, options.successMessage, options.errorMessage, toast]
  );

  const resetFieldErrors = useCallback(() => setFieldErrors({}), []);

  return { run, loading, fieldErrors, resetFieldErrors };
}
